import React, { useState, useEffect } from 'react'
import { Box, Paper, Typography, Skeleton, useTheme, alpha, Grid, LinearProgress } from '@mui/material'
import { Globe, Users, Clock, TrendingDown, Link } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { analyticsApi } from '../../../../api/analyticsApi'
import KPICard from '../cards/KPICard'
import ActiveUsersChart from '../charts/ActiveUsersChart'

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (mins === 0) return `${secs}s`
  return `${mins}m ${secs}s`
}

const formatBounceRate = (val) => {
  if (val === null || val === undefined) return '0%'
  return `${val}%`
}

const LandingPageTab = ({ period }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await analyticsApi.getLanding(period)
        setData(res.data)
      } catch (err) {
        console.error('Failed to fetch landing page data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [period])

  const kpiCards = [
    { label: t('analytics_total_visits'), icon: Globe, metric: data?.total_visits },
    { label: t('analytics_unique_visitors'), icon: Users, metric: data?.unique_visitors },
    { label: t('analytics_avg_time_on_page'), icon: Clock, metric: data?.avg_time_on_page, format: formatDuration },
    { label: t('analytics_bounce_rate'), icon: TrendingDown, metric: data?.bounce_rate, format: formatBounceRate },
    {
      label: t('analytics_top_referrer'),
      icon: Link,
      metric: {
        value: data?.top_referrer?.referrer || data?.top_referrer || '-',
        change_pct: null,
      },
    },
  ]

  const pageViewsTrend = data?.page_views_trend || []
  const ctaPerformance = data?.cta_performance || []
  const scrollDepth = data?.scroll_depth || {}
  const utmBreakdown = data?.utm_breakdown || []
  const topReferrers = data?.top_referrers || []

  const scrollLevels = [
    { label: '25%', value: scrollDepth['25'] || scrollDepth.pct_25 || 0 },
    { label: '50%', value: scrollDepth['50'] || scrollDepth.pct_50 || 0 },
    { label: '75%', value: scrollDepth['75'] || scrollDepth.pct_75 || 0 },
    { label: '100%', value: scrollDepth['100'] || scrollDepth.pct_100 || 0 },
  ]

  const tableHeaderSx = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '0.68rem',
    letterSpacing: '0.05em',
  }

  return (
    <Box
      sx={{
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} sm={6} md={2.4} key={card.label}>
            <KPICard
              label={card.label}
              icon={card.icon}
              metric={card.metric}
              accentColor={goldColor}
              loading={loading}
              format={card.format}
            />
          </Grid>
        ))}
      </Grid>

      {/* Page Views Trend */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('analytics_page_views_trend')}
        </Typography>
        <ActiveUsersChart
          data={pageViewsTrend}
          loading={loading}
          height={280}
          title="Page Views"
        />
      </Paper>

      {/* CTA Performance */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('analytics_cta_performance')}
        </Typography>

        {loading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="text" width="100%" height={36} sx={{ mb: 0.5 }} />
            ))}
          </>
        ) : ctaPerformance.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {t('analytics_no_cta_data')}
          </Typography>
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                py: 1,
                px: 1.5,
                borderRadius: '8px',
                background: alpha(theme.palette.text.primary, 0.03),
                mb: 0.5,
              }}
            >
              <Box sx={{ flex: 2 }}>
                <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_label')}</Typography>
              </Box>
              <Box sx={{ width: 100, textAlign: 'right' }}>
                <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_clicks')}</Typography>
              </Box>
              <Box sx={{ width: 100, textAlign: 'right' }}>
                <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_ctr')}</Typography>
              </Box>
            </Box>

            {ctaPerformance.map((cta, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  py: 1.2,
                  px: 1.5,
                  borderRadius: '6px',
                  transition: 'background 0.15s',
                  '&:hover': { background: alpha(theme.palette.text.primary, 0.02) },
                }}
              >
                <Box sx={{ flex: 2, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: theme.palette.text.primary }}>
                    {cta.label || cta.name}
                  </Typography>
                </Box>
                <Box sx={{ width: 100, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: theme.palette.text.primary }}>
                    {(cta.clicks || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ width: 100, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: goldColor, fontWeight: 600 }}>
                    {cta.ctr ?? cta.click_through_rate ?? 0}%
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Paper>

      {/* Scroll Depth */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('analytics_scroll_depth')}
        </Typography>

        {loading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={28} sx={{ mb: 1.5, borderRadius: '6px' }} />
            ))}
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {scrollLevels.map((level) => (
              <Box key={level.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, color: theme.palette.text.primary }}>
                    {level.label} {t('analytics_pct_reached')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: goldColor }}>
                    {level.value}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(level.value, 100)}
                  sx={{
                    height: 8,
                    borderRadius: '4px',
                    background: alpha(goldColor, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: '4px',
                      background: `linear-gradient(90deg, ${alpha(goldColor, 0.6)}, ${goldColor})`,
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* UTM Breakdown & Top Referrers - side by side */}
      <Grid container spacing={3}>
        {/* UTM Breakdown */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t('analytics_utm_breakdown')}
            </Typography>

            {loading ? (
              <>
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="text" width="100%" height={36} sx={{ mb: 0.5 }} />
                ))}
              </>
            ) : utmBreakdown.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {t('analytics_no_utm_data')}
              </Typography>
            ) : (
              <>
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    py: 1,
                    px: 1.5,
                    borderRadius: '8px',
                    background: alpha(theme.palette.text.primary, 0.03),
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_source')}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_medium')}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_campaign')}</Typography>
                  </Box>
                  <Box sx={{ width: 80, textAlign: 'right' }}>
                    <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_visits')}</Typography>
                  </Box>
                </Box>

                {utmBreakdown.map((utm, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      py: 1.2,
                      px: 1.5,
                      borderRadius: '6px',
                      transition: 'background 0.15s',
                      '&:hover': { background: alpha(theme.palette.text.primary, 0.02) },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {utm.source || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>
                        {utm.medium || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {utm.campaign || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 80, textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: theme.palette.text.primary }}>
                        {(utm.visits || utm.count || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Paper>
        </Grid>

        {/* Top Referrers */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t('analytics_top_referrers')}
            </Typography>

            {loading ? (
              <>
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} variant="text" width="100%" height={36} sx={{ mb: 0.5 }} />
                ))}
              </>
            ) : topReferrers.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {t('analytics_no_referrer_data')}
              </Typography>
            ) : (
              <>
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    py: 1,
                    px: 1.5,
                    borderRadius: '8px',
                    background: alpha(theme.palette.text.primary, 0.03),
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_referrer')}</Typography>
                  </Box>
                  <Box sx={{ width: 80, textAlign: 'right' }}>
                    <Typography variant="caption" sx={tableHeaderSx}>{t('analytics_visits')}</Typography>
                  </Box>
                </Box>

                {topReferrers.map((ref, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      py: 1.2,
                      px: 1.5,
                      borderRadius: '6px',
                      transition: 'background 0.15s',
                      '&:hover': { background: alpha(theme.palette.text.primary, 0.02) },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.82rem', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ref.referrer || ref.source || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 80, textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: theme.palette.text.primary }}>
                        {(ref.visits || ref.count || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default LandingPageTab
